import React, { useEffect, useRef } from "react";

import type { Row, RowData, Table } from "@tanstack/react-table-v9";

import TableV2IconButton, { toTestId } from "./TableV2IconButton";
import Checkbox from "../../Checkbox/Checkbox";
import type { TableV2Features } from "../features";

interface TriStateCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  label: string;
  onClick?: (event: unknown) => void;
}

export const TriStateCheckbox = ({
  checked,
  indeterminate = false,
  disabled,
  label,
  onClick,
}: TriStateCheckboxProps) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <Checkbox
      showIndeterminate
      controlled={false}
      resolvedRef={ref as React.RefObject<HTMLInputElement>}
      disabled={disabled}
      // onClick (not onChange) keeps the Shift key for range selection
      rest={{
        checked,
        onClick,
        "data-testid": toTestId(label),
        readOnly: true,
      }}
    />
  );
};

export const RowCheckbox = <TData extends RowData>({
  row,
}: {
  row: Row<TableV2Features, TData>;
}) => {
  const canSelect = row.getCanSelect();
  return (
    <TriStateCheckbox
      checked={canSelect && row.getIsSelected()}
      indeterminate={row.getIsSomeSelected()}
      disabled={!canSelect}
      label="Select row"
      onClick={canSelect ? row.getToggleSelectedHandler() : undefined}
    />
  );
};

export interface SelectAllOptions {
  onSelectAllRows?: (selected: boolean) => void;
  allRowsSelected?: boolean;
}

export const SelectAllCheckbox = <TData extends RowData>({
  table,
}: {
  table: Table<TableV2Features, TData>;
}) => {
  const { onSelectAllRows, allRowsSelected } =
    table.options as SelectAllOptions;
  if (table.options.enableMultiRowSelection === false) return null;
  // A controlled query-wide selection outlives the page, so it decides the header's state
  const queryWide = allRowsSelected !== undefined;
  const checked = queryWide ? allRowsSelected : table.getIsAllRowsSelected();
  return (
    <TriStateCheckbox
      checked={checked}
      indeterminate={!checked && table.getIsSomeRowsSelected()}
      label="Select all rows"
      onClick={(event) => {
        if (queryWide && checked) table.resetRowSelection(true);
        else if (queryWide) table.toggleAllRowsSelected(true);
        else table.getToggleAllRowsSelectedHandler()(event);
        onSelectAllRows?.(!checked);
      }}
    />
  );
};

export const ExpandToggle = <TData extends RowData>({
  row,
}: {
  row: Row<TableV2Features, TData>;
}) => {
  if (!row.getCanExpand()) return null;
  const expanded = row.getIsExpanded();
  return (
    <TableV2IconButton
      tooltip={false}
      icon={expanded ? "fa-regular fa-minus" : "fa-regular fa-plus"}
      label={expanded ? "Collapse row" : "Expand row"}
      onClick={(event) => {
        event.stopPropagation();
        row.toggleExpanded();
      }}
    />
  );
};

export const ExpandAllToggle = <TData extends RowData>({
  table,
}: {
  table: Table<TableV2Features, TData>;
}) => {
  if (!table.getCanSomeRowsExpand()) return null;
  const expanded = table.getIsAllRowsExpanded();
  return (
    <TableV2IconButton
      tooltip={false}
      icon={
        expanded
          ? "fa-solid fa-compress-arrows-alt"
          : "fa-solid fa-expand-arrows-alt"
      }
      label={expanded ? "Collapse all rows" : "Expand all rows"}
      onClick={(event) => {
        event.stopPropagation();
        table.toggleAllRowsExpanded(!expanded);
      }}
    />
  );
};
